import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { forwardRef, useRef, useState } from 'react';
import { compact } from 'lodash-es';
import { BottomSheetContentLayout } from '../BottomSheet';
import { useSheetStack } from '../OverlayContext/hooks/useSheetStack';
import { Typography } from '../Typography';
import { BaseTextField } from './BaseTextField/BaseTextField';
import { MobileTextFieldTID } from './BaseTextField/BaseTextField.utils';
import { MobileSummaryCard } from './MobileSummaryCard';
import styles from './MobileTextField.module.scss';
const TextFieldInBottomSheet = /*#__PURE__*/ forwardRef(function TextFieldInBottomSheet(props, ref) {
    var _ref;
    var _props_mobile, _props_mobile1;
    const { validateInBottomSheet, width: _width, minWidth: _minWidth, InputProps: outerInputProps } = props, rest = _object_without_properties(props, [
        "validateInBottomSheet",
        "width",
        "minWidth",
        "InputProps"
    ]);
    const { hideBottomSheet } = useSheetStack();
    const [tempValue, setTempValue] = useState(props.value);
    const sheetInputRef = useRef(null);
    const renderSheetEndAdornment = (_props_mobile = props.mobile) === null || _props_mobile === void 0 ? void 0 : _props_mobile.renderSheetEndAdornment;
    // WHY: the outer field's adornment is bound to the outer input and setter, so reusing it here
    // would edit the field behind the sheet and be overwritten on save.
    const sheetInputProps = renderSheetEndAdornment ? _object_spread_props(_object_spread({}, outerInputProps), {
        endAdornment: renderSheetEndAdornment({
            inputRef: sheetInputRef,
            setValue: setTempValue
        })
    }) : outerInputProps;
    // WHY: consumers bind their own inputRef to the sheet's input — compose, never replace.
    const bindSheetInput = (element)=>{
        sheetInputRef.current = element;
        const consumerInputRef = rest.inputRef;
        if (typeof consumerInputRef === 'function') consumerInputRef(element);
        else if (consumerInputRef && typeof consumerInputRef === 'object') consumerInputRef.current = element;
    };
    const validationResult = validateInBottomSheet === null || validateInBottomSheet === void 0 ? void 0 : validateInBottomSheet(tempValue);
    const error = validationResult ? validationResult.error : props.error;
    const helperText = validationResult ? validationResult.helperText : props.helperText;
    function handleSave() {
        if (tempValue !== props.value && props.onChange) {
            props.onChange({
                target: {
                    value: tempValue
                }
            });
        }
        hideBottomSheet();
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentClassName: styles.bottomSheetContent,
        footer: {
            primaryAction: {
                text: (_ref = (_props_mobile1 = props.mobile) === null || _props_mobile1 === void 0 ? void 0 : _props_mobile1.saveButtonLabel) !== null && _ref !== void 0 ? _ref : 'Save',
                onClick: ()=>handleSave(),
                bi: {
                    name: 'save'
                }
            }
        },
        children: /*#__PURE__*/ _jsx(BaseTextField, _object_spread_props(_object_spread({}, rest), {
            collapseHelperText: true,
            value: tempValue,
            error: error,
            helperText: helperText,
            onChange: (e)=>setTempValue(e.target.value),
            onKeyDown: ()=>{},
            InputProps: sheetInputProps,
            inputRef: bindSheetInput,
            ref: ref
        }))
    });
});
// See note on BaseTextField — forwardRef + MUI types collapses BiProps' discriminated union.
const MobileTextFieldImpl = /*#__PURE__*/ forwardRef(function MobileTextField(props, ref) {
    var _ref;
    const { testId, error, label, value, helperText, disabled, mobile } = props;
    const { pushBottomSheetPage } = useSheetStack();
    return /*#__PURE__*/ _jsx(MobileSummaryCard, {
        testId: testId,
        items: [
            {
                title: label,
                titleTestId: MobileTextFieldTID.Label(testId),
                body: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            testId: MobileTextFieldTID.Value(testId),
                            skin: error ? 'error' : 'standard',
                            children: (_ref = mobile === null || mobile === void 0 ? void 0 : mobile.displayValue) !== null && _ref !== void 0 ? _ref : value
                        }),
                        helperText && /*#__PURE__*/ _jsx(Typography, {
                            testId: MobileTextFieldTID.HelperText(testId),
                            skin: error ? 'error' : 'secondary',
                            children: helperText
                        })
                    ]
                }),
                actions: compact([
                    !disabled && {
                        type: 'edit',
                        testId: MobileTextFieldTID.EditButton(testId),
                        onClick: ()=>{
                            pushBottomSheetPage({
                                content: /*#__PURE__*/ _jsx(TextFieldInBottomSheet, _object_spread_props(_object_spread({}, props), {
                                    ref: ref
                                }))
                            });
                        },
                        // WHY: inherent action opening the edit bottom-sheet; consumers cannot rename it (rule 6a) and it dispatches no onChange, so it cannot double-fire.
                        bi: {
                            name: 'edit'
                        }
                    }
                ])
            }
        ]
    });
});
export const MobileTextField = MobileTextFieldImpl;
